import React, { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { checkmarkCircleOutline, chevronDownOutline, golfOutline, personOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { GamePlayer, GameSetup } from './types';

interface PlayerDetailsProps {
  initialData: Partial<GameSetup>;
  onNext: (data: Partial<GameSetup>) => void;
  onBack: () => void;
}

const PlayerDetails: React.FC<PlayerDetailsProps> = ({ initialData, onNext, onBack }) => {
  const { t } = useTranslation();
  const [players, setPlayers] = useState<GamePlayer[]>(initialData.players || []);
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  const [pickerState, setPickerState] = useState<{
    playerId: string;
    type: 'tee' | 'startingHole';
    isOpen: boolean;
  }>({ playerId: '', type: 'tee', isOpen: false });

  const togglePlayerExpanded = (playerId: string) => {
    setExpandedPlayer(expandedPlayer === playerId ? null : playerId);
  };

  const updatePlayerHandicap = (playerId: string, handicap: string) => {
    const handicapValue = handicap === '' ? undefined : parseInt(handicap);
    setPlayers(
      players.map(player =>
        player.id === playerId
          ? { ...player, handicap: handicapValue }
          : player
      )
    );
  };

  const updatePlayerTee = (playerId: string, tee: 'white' | 'yellow' | 'red' | 'blue') => {
    setPlayers(
      players.map(player =>
        player.id === playerId
          ? { ...player, teeBox: tee }
          : player
      )
    );
  };

  const updatePlayerStartingHole = (playerId: string, hole: number) => {
    setPlayers(
      players.map(player =>
        player.id === playerId
          ? { ...player, startingHole: hole }
          : player
      )
    );
  };

  const handleSubmit = () => {
    onNext({
      ...initialData,
      players,
    });
  };

  // No longer needed as we're using custom pickers

  return (
    <div className="flex flex-col h-full p-4">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
      >
        <h2 className="text-xl font-semibold">{t('Player Details')}</h2>
        <p className="text-muted-foreground text-sm">
          {t('Customize settings for each player')}
        </p>
      </motion.div>

      <div className="flex-1 overflow-auto">
        <div className="space-y-3">
          {players.map((player) => (
            <motion.div
              key={player.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800"
            >
              <button
                onClick={() => togglePlayerExpanded(player.id)}
                className="w-full p-4 flex items-center justify-between text-left"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
                    <IonIcon icon={personOutline} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="font-medium">{player.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                      {player.handicap !== undefined && (
                        <span className="mr-2">{t('Handicap')}: {player.handicap}</span>
                      )}
                      {player.teeBox && (
                        <span className="flex items-center mr-2">
                          <span className={`inline-block w-2 h-2 rounded-full mr-1 ${
                            player.teeBox === 'white' ? 'bg-white border border-gray-300' :
                            player.teeBox === 'yellow' ? 'bg-yellow-400' :
                            player.teeBox === 'red' ? 'bg-red-500' :
                            'bg-blue-500'
                          }`}></span>
                          {t(player.teeBox.charAt(0).toUpperCase() + player.teeBox.slice(1))}
                        </span>
                      )}
                      {player.startingHole && (
                        <span>{t('Hole')}: {player.startingHole}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  {expandedPlayer === player.id ? (
                    <IonIcon icon={checkmarkCircleOutline} className="text-green-500 size-5 mr-2" />
                  ) : null}
                  <IonIcon
                    icon={chevronDownOutline}
                    className={`text-gray-400 size-5 transition-transform ${
                      expandedPlayer === player.id ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </button>
              
              <AnimatePresence>
                {expandedPlayer === player.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 pt-0 space-y-4 bg-gray-50 dark:bg-gray-800/50">
                      {/* Handicap */}
                      <div>
                        <label htmlFor={`handicap-${player.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                          {t('Handicap')}
                        </label>
                        <input
                          id={`handicap-${player.id}`}
                          type="number"
                          value={player.handicap === undefined ? '' : player.handicap}
                          onChange={(e) => updatePlayerHandicap(player.id, e.target.value)}
                          placeholder={t('Enter handicap')}
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                        />
                      </div>
                      
                      {/* Tee Box */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                          {t('Tee Box')}
                        </label>
                        <button
                          onClick={() => setPickerState({ playerId: player.id, type: 'tee', isOpen: true })}
                          className="w-full flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-left"
                        >
                          <div className="flex items-center">
                            {player.teeBox && (
                              <span className={`inline-block w-4 h-4 rounded-full mr-2 ${
                                player.teeBox === 'white' ? 'bg-white border border-gray-300' :
                                player.teeBox === 'yellow' ? 'bg-yellow-400' :
                                player.teeBox === 'red' ? 'bg-red-500' :
                                'bg-blue-500'
                              }`}></span>
                            )}
                            <span>
                              {player.teeBox 
                                ? t(player.teeBox.charAt(0).toUpperCase() + player.teeBox.slice(1)) 
                                : t('Select tee')}
                            </span>
                          </div>
                          <IonIcon icon={chevronDownOutline} className="text-gray-400" />
                        </button>
                      </div>
                      
                      {/* Starting Hole */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                          {t('Starting Hole')}
                        </label>
                        <button
                          onClick={() => setPickerState({ playerId: player.id, type: 'startingHole', isOpen: true })}
                          className="w-full flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-left"
                        >
                          <span>
                            {player.startingHole 
                              ? `${t('Hole')} ${player.startingHole}` 
                              : t('Select starting hole')}
                          </span>
                          <IonIcon icon={golfOutline} className="text-gray-400" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
      
      <div className="mt-auto pt-4 grid grid-cols-2 gap-3">
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={onBack}
          className="py-3 px-4 rounded-xl font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          {t('Back')}
        </motion.button>
        
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          onClick={handleSubmit}
          className="py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors"
        >
          {t('Continue')}
        </motion.button>
      </div>
      
      {/* Tee Box Picker */}
      {pickerState.isOpen && pickerState.type === 'tee' && (
        <div className="picker-wrapper">
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
            <div className="bg-white dark:bg-gray-900 rounded-t-2xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <button 
                  className="text-blue-500 font-medium"
                  onClick={() => setPickerState({ ...pickerState, isOpen: false })}
                >
                  {t('Cancel')}
                </button>
                <h3 className="text-center font-medium">{t('Tee Box')}</h3>
                <button 
                  className="text-blue-500 font-medium"
                  onClick={() => {
                    // Keep current value
                    setPickerState({ ...pickerState, isOpen: false });
                  }}
                >
                  {t('Done')}
                </button>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: t('White'), value: 'white', color: 'bg-white border border-gray-300' },
                    { label: t('Yellow'), value: 'yellow', color: 'bg-yellow-400' },
                    { label: t('Red'), value: 'red', color: 'bg-red-500' },
                    { label: t('Blue'), value: 'blue', color: 'bg-blue-500' }
                  ].map(tee => (
                    <button
                      key={tee.value}
                      className="p-3 rounded-lg flex items-center space-x-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => {
                        updatePlayerTee(pickerState.playerId, tee.value as 'white' | 'yellow' | 'red' | 'blue');
                        setPickerState({ ...pickerState, isOpen: false });
                      }}
                    >
                      <span className={`inline-block w-4 h-4 rounded-full ${tee.color}`}></span>
                      <span>{tee.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Starting Hole Picker */}
      {pickerState.isOpen && pickerState.type === 'startingHole' && (
        <div className="picker-wrapper">
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
            <div className="bg-white dark:bg-gray-900 rounded-t-2xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <button 
                  className="text-blue-500 font-medium"
                  onClick={() => setPickerState({ ...pickerState, isOpen: false })}
                >
                  {t('Cancel')}
                </button>
                <h3 className="text-center font-medium">{t('Starting Hole')}</h3>
                <button 
                  className="text-blue-500 font-medium"
                  onClick={() => {
                    // Keep current value
                    setPickerState({ ...pickerState, isOpen: false });
                  }}
                >
                  {t('Done')}
                </button>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-3 gap-3">
                  {Array.from({ length: initialData.holes || 18 }, (_, i) => i + 1).map(hole => (
                    <button
                      key={hole}
                      className="p-3 rounded-lg text-center bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => {
                        updatePlayerStartingHole(pickerState.playerId, hole);
                        setPickerState({ ...pickerState, isOpen: false });
                      }}
                    >
                      {hole}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerDetails;
